import { RewardReference } from "../../handlers/types";
import StageService from "../../services/StageService";
import { useGameStore } from "../../store/gameStore";
import { profiler } from "../../utils/PerformanceProfiler";
import WEAPONS from "../consts/WEAPONS";
import DestructibleType from "../enums/DestructibleType";
import HitVFXType from "../enums/HitVFXType";
import PickupType from "../enums/PickupType";
import Scenes from "../enums/Scenes";
import { default as Weapons, default as WeaponType } from "../enums/WeaponType";
import BGMan from "./BGMan";
import { Character } from "./character/Character";
import ContainmentRect from "./ContainmentRect";
import DamageNumberGroup from "./damage/DamageNumberGroup";
import DestructibleGroup from "./destructible/DestructibleGroup";
import Enemy from "./enemy/Enemy";
import FormationManager from "./formations/FormationManager";
import Game from "./Game";
import LevelUpFactory, { WeaponData } from "./LevelUpFactory";
import LootManager from "./LootManager";
import Magnet from "./Magnet";
import NPCGroup from "./npc/NPCGroup";
import BasePickup from "./pickup/BasePickup";
import CoinCounterGroup from "./pickup/CoinCounterGroup";
import GemPickup from "./pickup/GemPickup";
import PickupGroup from "./pickup/PickupGroup";
import TreasurePickup from "./pickup/TreasurePickup";
import Player from "./Player";
import PlayerOptions from "./PlayerOptions";
import SceneManager from "./SceneManager";
import Stage from "./Stage";
import MainUi from "./ui/MainUI";
import PlayerUI from "./ui/PlayerUI";
import BaseBullet from "./weapon/BaseBullet";
import BaseWeapon from "./weapon/BaseWeapon";
import WeaponFactory from "./weapon/WeaponFactory";
import { getCurrentPerformanceConfig } from "../../utils/performance";
import SpatialGrid from "./SpatialGrid";
import PoolingFacade from "./pooling/PoolingFacade";

export default class GameCore {
  Player: Player;
  Magnet: Magnet;
  MainUI: MainUi;
  pickupPool: PickupGroup;
  gemsPool: PickupGroup;
  game: any;
  scene: Phaser.Scene;
  MaxGems: number;
  SurvivedSeconds: number;
  MaxDestructibles: number;
  IsTimeStopped: boolean;
  TimeStopConfig: Phaser.Types.Time.TimerEventConfig;

  PlayerOptions: PlayerOptions;
  WeaponFactory: WeaponFactory;
  LevelUpFactory: LevelUpFactory;
  LootManager: LootManager;
  SceneManager: SceneManager;
  EnemyGroup: Phaser.GameObjects.Group;
  WeaponGroup: Phaser.GameObjects.Group;
  BulletGroup: Phaser.GameObjects.Group;
  PickupGroup: Phaser.GameObjects.Group;
  tickerEvent: Phaser.Time.TimerEvent;
  Stage: Stage;
  BGMan: BGMan;
  Weapons: BaseWeapon[];
  Enemies: Enemy[];
  Destructibles: any[];
  containmentRect_Screen: ContainmentRect;
  PfxEmitter_Pickups: Phaser.GameObjects.Particles.ParticleEmitter | undefined;
  // Prevent processing pickups when finishing the run
  private acceptPickups: boolean = true;
  PlayerUI: PlayerUI;
  LevelUpScene: Phaser.Scene;
  TreasureScene: Phaser.Scene;
  destructiblesPool: DestructibleGroup;
  npcPool: NPCGroup;
  damageNumberPool: DamageNumberGroup;
  formationManager: FormationManager;
  SelectedCharacterId: string;
  StageService: ReturnType<typeof StageService.getInstance>;
  stageEndTime: number;
  private magnetPickupOverlap: Phaser.Physics.Arcade.Collider | null = null;
  private static _basePlayerPxSpeed = 150;
  public static get PlayerPxSpeed() {
    return GameCore._basePlayerPxSpeed;
  }

  public static set PlayerPxSpeed(value) {
    GameCore._basePlayerPxSpeed = value;
  }

  private static _baseEnemySpeed = 0.6;
  public static get EnemySpeed() {
    return GameCore._baseEnemySpeed;
  }

  public static set EnemySpeed(value) {
    GameCore._baseEnemySpeed = value;
  }

  private static _baseProjectileSpeed = 165;
  public static get ProjectileSpeed() {
    return GameCore._baseProjectileSpeed;
  }

  public static set ProjectileSpeed(value) {
    GameCore._baseProjectileSpeed = value;
  }

  static PixelScale = 1;
  static RpixelScale = 1;
  static ZDamageNumber = Number.MAX_SAFE_INTEGER - 1e4;
  static ZInGameUI = Number.MAX_SAFE_INTEGER - 1000;

  // Performance configuration - now using global performance system
  private static getPerformanceConfig() {
    return getCurrentPerformanceConfig();
  }

  // Get current performance limits
  public static get MAX_BULLETS() {
    const config = GameCore.getPerformanceConfig();
    return config.bulletsLimit;
  }

  public static get MAX_GEMS() {
    const config = GameCore.getPerformanceConfig();
    return config.gemsLimit;
  }

  public static get MAX_COINS() {
    const config = GameCore.getPerformanceConfig();
    return config.coinsLimit;
  }

  coinCounters: CoinCounterGroup;

  // Gift coins tracking
  giftCoinsCollected: number = 0;

  // Caravan coins tracking
  caravanCoinsCollected: number = 0;

  // Spatial grids for efficient collision detection and queries
  enemySpatialGrid: SpatialGrid<Enemy>;
  bulletSpatialGrid: SpatialGrid<BaseBullet>;

  // Object pooling manager for performance optimization
  poolManager: PoolingFacade;

  // Magnet state for revival preservation
  private magnetState: {
    radius: number;
  } | null = null;

  constructor(game: Phaser.Game, scene: Phaser.Scene) {
    this.game = game;
    this.scene = scene;
    this.MaxGems = 200;
    this.SurvivedSeconds = 0;
    this.MaxDestructibles = 10;
    this.IsTimeStopped = false;

    this.PlayerOptions = new PlayerOptions();
    this.WeaponFactory = new WeaponFactory();
    this.LevelUpFactory = new LevelUpFactory();
    this.LootManager = new LootManager();
    this.SceneManager = new SceneManager(this.game, this.scene.scene);
    this.Weapons = [];
    this.Enemies = [];
    this.Destructibles = [];
    this.PlayerUI = new PlayerUI(this.scene);
    this.StageService = StageService.getInstance();

    // Initialize spatial grids
    this.enemySpatialGrid = new SpatialGrid<Enemy>(120); // 120px cells for enemies
    this.bulletSpatialGrid = new SpatialGrid<BaseBullet>(80); // 80px cells for bullets

    // Set up off-screen detection for optimization
    this.enemySpatialGrid.setOffScreenCheck((enemy) => {
      return this.containmentRect_Screen
        ? !this.containmentRect_Screen.Contains(enemy)
        : false;
    });
  }

  async initialize(): Promise<void> {
    // Store is now initialized by React GameStoreProvider
    // Just verify it is available
    const store = useGameStore.getState();

    if (!store.isInitialized) {
      throw new Error(
        "Store not initialized. Make sure GameStoreProvider is mounted."
      );
    }

    this.scene.scale.lockOrientation("portrait");

    this.coinCounters = new CoinCounterGroup(this.scene);
    this.coinCounters.Init();
  }

  // Store-based methods to replace old services
  get UserService() {
    return {
      loadUser: async () => {
        const store = useGameStore.getState();
        await store.refreshUserData();
        return store.user;
      },
    };
  }

  get UserCharactersService() {
    return {
      getCharacter: () => useGameStore.getState().character,
      getUpgradesInfo: () => useGameStore.getState().upgradesInfo,
      updateCharacter: async (updates: any) => {
        // Updates are handled by the store
        const store = useGameStore.getState();
        await store.refreshUpgrades();
      },
    };
  }

  get UserLooksService() {
    return {
      getSelectedLook: () => {
        const store = useGameStore.getState();
        const selectedLookId = store.selectedLookId;
        return (
          store.userLooks.find((look) => look.id === selectedLookId) || null
        );
      },
      selectLook: async (lookId: string) => {
        return await useGameStore.getState().selectLook(lookId);
      },
    };
  }

  CleanUp() {
    this.WeaponFactory = new WeaponFactory();
    this.LevelUpFactory = new LevelUpFactory();
    this.LootManager = new LootManager();
    this.Enemies = [];
    this.Weapons = [];
    this.Destructibles = [];
    this.SurvivedSeconds = 0;
    this.IsTimeStopped = false;
    this.PlayerOptions.RunCoins = 0;
    this.PlayerOptions.Runenemies = 0;

    // Clean up physics world colliders and overlaps
    this.scene?.physics?.world?.colliders?.destroy();

    this.EnemyGroup.clear();
    this.PickupGroup.clear();
    this.BulletGroup.clear();
    this.destructiblesPool.clear();
    this.pickupPool?.clearAll();
    this.gemsPool?.clearAll();
    this.formationManager?.clear();

    // Clear spatial grids
    this.enemySpatialGrid?.clear();
    this.bulletSpatialGrid?.clear();
    this.giftCoinsCollected = 0;
    this.caravanCoinsCollected = 0;

    // Clear preserved magnet state (for full cleanup, not revival)
    this.magnetState = null;

    // Clean up the ticker event
    if (this.tickerEvent) {
      this.tickerEvent.remove();
      this.tickerEvent = null as unknown as Phaser.Time.TimerEvent;
    }

    // Clean up the magnet to prevent memory leaks
    if (this.Magnet) {
      this.Magnet.destroy();
      // Initialize a new Magnet instead of setting to undefined to maintain type safety
      this.Magnet = new Magnet(this.scene, 0, 0);
    }

    // Clean up the particle emitter
    if (this.PfxEmitter_Pickups) {
      this.PfxEmitter_Pickups.destroy();
      this.PfxEmitter_Pickups = undefined;
    }
  }

  InitGame(scene: Phaser.Scene) {
    // Fresh run accepts pickups
    this.acceptPickups = true;
    const characterData = this.UserCharactersService?.getCharacter();

    if (!characterData) {
      console.error("No character data found");
      return;
    }

    //this.Player = new Player(scene, scene.cameras.main.centerX, scene.cameras.main.centerY, characterData)
    this.scene = scene;
    this.formationManager = new FormationManager(this.scene);
    this.poolManager = new PoolingFacade(this.scene);
    this.EnemyGroup = this.scene.add.group();
    this.BulletGroup = this.scene.add.group();
    this.PickupGroup = this.scene.add.group();
    this.pickupPool = new PickupGroup(this.scene, {
      maxSize: { [PickupType.COIN]: GameCore.MAX_COINS }, // Performance-based limit with distance-based cleanup
    });
    this.gemsPool = new PickupGroup(this.scene, {
      maxSize: { [PickupType.GEM]: GameCore.MAX_GEMS },
    });
    this.destructiblesPool = new DestructibleGroup(this.scene);
    this.npcPool = new NPCGroup(this.scene);

    // Physics optimizations
    this.scene.physics.world.setBounds(0, 0, 0, 0);

    // Optimize physics settings for better performance
    this.scene.physics.world.timeScale = 1;
    this.scene.physics.world.setBoundsCollision(false, false, false, false);

    // this.EnemyGroup['physicsType'] = Phaser.Physics.Arcade.DYNAMIC_BODY
    this.CleanUp();
    this.MakePlayer(characterData);
    this.scene.physics.add.overlap(this.Player, this.EnemyGroup, (obj1, obj2) =>
      this.onPlayerOverlapsEnemy(obj1, obj2)
    );
    this.scene.physics.add.collider(this.EnemyGroup, this.EnemyGroup);
    this.scene.physics.add.overlap(
      this.Player,
      this.PickupGroup,
      (obj1, obj2) => this.onPlayerOverlapsPickup(obj1, obj2)
    );
    this.magnetPickupOverlap = this.scene.physics.add.overlap(
      this.Magnet,
      this.PickupGroup,
      (magnet, pickup) => {
        // Type assertion to handle StaticBody
        const magnetObj = magnet as any;
        const pickupObj = pickup as any;
        this.onMagnetOverlapsPickup(magnetObj, pickupObj);
      }
    );
    // this.scene.physics.add.collider(this.Player, this.npcPool, this.onPlayerOverlapsNPC.bind(this))
    // this.hitVFXPool = new At(e),

    if (localStorage.getItem("debugMode") === "true") {
      this.scene.physics.world.createDebugGraphic();
    }

    this.damageNumberPool = new DamageNumberGroup(this.scene);
    this.coinCounters = new CoinCounterGroup(this.scene);
    this.coinCounters.Init();

    this.tickerEvent = this.scene.time.addEvent({
      delay: 1000,
      callback: this.OnTickerEvent,
      callbackScope: this,
      loop: true,
    });
    this.MainUI = new MainUi(this.scene);
    this.MainUI.UpdateCoins();
    this.MainUI.UpdateKills();
    this.Player.MakeLevelOne();
    this.LevelUpFactory.init();

    // Initialize particle emitter once with transparent particles
    this.PfxEmitter_Pickups = this.scene.add.particles(0, 0, undefined, {
      lifespan: 800,
      frequency: 100,
      maxParticles: 100,
      // No visible particles, just for game logic
      alpha: { start: 0, end: 0 },
    });

    this.Stage = new Stage(this.scene, this.PlayerOptions.SelectedStage);
    this.Stage.Init();
    this.stageEndTime = this.Stage.getDeathAppearanceTime();
    this.AddWeapon(this.Player.startingWeapon);
    this.LootManager.Init();

    this.BGMan = new BGMan(this.scene);
    const selectedStageId = String(this.PlayerOptions?.SelectedStage || "");
    const useGreenBg = selectedStageId.startsWith("G");
    this.BGMan.MakeBackground(useGreenBg ? "bgTileGreen" : "bgTile");
    this.containmentRect_Screen = new ContainmentRect(0.6);
    this.PlayerUI.UpdatePlayerLevel();
    this.PlayerUI.Update();
    this.LevelUpScene = this.scene.scene.get(Scenes.UI_levelUpScene);
    this.TreasureScene = this.scene.scene.get(Scenes.UI_treasureScene);

    // test code
    // this.MakeNPC(NPCType.FAIRY)
    // this.MakeNPC(NPCType.VAMPRIE)
    // this.MakeNPC(NPCType.BUSINESSMAN)
    // end test code

    if (localStorage.getItem("testEvolution") === "true") {
      const weapon = this.GetWeapon(WeaponType.ROCK);
      weapon.level = 6;
      this.Weapons.push(weapon);
      this.MainUI?.AddWeaponIcon(WeaponType.ROCK);

      const weapon2 = this.GetWeapon(WeaponType.COOLDOWN);
      weapon2.level = 5;
      this.Weapons.push(weapon2);
      this.MainUI?.AddWeaponIcon(WeaponType.COOLDOWN);
    }
  }

  FreezeSnapshot(callback: () => void) {
    if (callback) {
      callback();
    }
  }
  AddWeapon(bulletType: Weapons, isLevelUp: boolean = true) {
    const weapon = this.GetWeapon(bulletType);

    if (!isLevelUp) {
      weapon.level = 0;
    }
    this.Weapons.push(weapon);

    this.MainUI?.AddWeaponIcon(bulletType);
  }
  RemoveWeapon(bulletTypes: Weapons[]) {
    for (const bulletType of bulletTypes) {
      const weapon = this.Weapons.find((w) => w.bulletType === bulletType);
      if (weapon) {
        weapon.CleanUp();
        this.Weapons.splice(this.Weapons.indexOf(weapon), 1);

        // Also remove from UI if it exists
        if (this.MainUI) {
          this.MainUI.RemoveWeaponIcon(bulletType);
        }
      }
    }
  }
  GetWeapon(bulletType: Weapons) {
    return this.WeaponFactory.GetWeapon(bulletType);
  }
  MakePlayer(character: Character) {
    this.Player = new Player(this.scene, 0, 0, character);
    this.PlayerUI = new PlayerUI(this.scene);
    this.Magnet = new Magnet(this.scene, 0, 0);
    return this.Player;
  }
  MakeDestructible(type: DestructibleType) {
    const vector = this.GetPositionOutOfSight(45);
    this.destructiblesPool.SpawnAt(vector.x, vector.y, type);
    while (this.destructiblesPool.spawned.length > this.MaxDestructibles) {
      const furthest = this.furthest(
        this.Player,
        this.destructiblesPool.spawned
      );
      if (furthest) {
        furthest.DeSpawn();
      }
    }
  }

  // MakeNPC(type: NPCType) {
  //   const vector = this.GetPositionOutOfSight(45)
  //   const npc = this.npcPool.SpawnAt(vector.x, vector.y, type)
  //   while (this.npcPool.spawned.length > 1) {
  //     const furthest = this.furthest(this.Player, this.npcPool.spawned)
  //     if (furthest) {
  //       furthest.DeSpawn()
  //     }
  //   }
  //   return npc
  // }

  TurnOnVacuum() {
    const gems = this.gemsPool.spawned[PickupType.GEM];
    const gemsLength = gems.length;
    for (let i = 0; i < gemsLength; i++) {
      gems[i].goToPlayer = true;
    }
  }

  OnTickerEvent() {
    this.SurvivedSeconds++;
    this.MainUI.SetRemainingSeconds(this.getRemainingSeconds());
    if (this.SurvivedSeconds % 30 == 0) {
      this.Stage.CheckMinute();
    }
  }

  onPlayerOverlapsEnemy(object1: any, object2: any) {
    const player = object1 as Player;
    const enemy = object2 as Enemy;
    if (!(enemy.isDead || enemy.IsTimeStopped)) {
      player.GetDamaged(enemy.power);
    }
  }
  onPlayerOverlapsPickup(object1: any, object2: any) {
    if (!this.acceptPickups) {
      return;
    }

    const pickup = object2 as BasePickup;

    if (!pickup) {
      return;
    }
    // If BasePickup has guard, this is additional short-circuit to avoid work
    this.GetPickup(pickup);
    pickup.GetTaken();
  }
  // onPlayerOverlapsNPC(object1: any, object2: any) {
  //   const npc = object2 as BaseNPC
  //   if (!npc.isLeave) {
  //     npc.OnPlayerOverlaps()
  //   }
  // }
  GetPickup(pickup: BasePickup) {
    // throw new Error('Method not implemented.')
  }

  onMagnetOverlapsPickup(
    magnet:
      | Phaser.Types.Physics.Arcade.GameObjectWithBody
      | Phaser.Tilemaps.Tile
      | Phaser.Physics.Arcade.Body,
    pickupObj:
      | Phaser.Types.Physics.Arcade.GameObjectWithBody
      | Phaser.Tilemaps.Tile
      | Phaser.Physics.Arcade.Body
  ) {
    if (!this.acceptPickups) {
      return;
    }

    const magnetSprite = magnet as unknown as Magnet;
    const pickup = pickupObj as unknown as BasePickup;

    if (!pickup) {
      return;
    }

    if (!pickup.Vacuum()) return;

    // Just emit particles at the pickup position, no need to recreate the emitter
    if (this.PfxEmitter_Pickups) {
      this.PfxEmitter_Pickups.emitParticleAt(pickup.x, pickup.y, 1);
    }
  }

  ResetWeaponCooldowns() {
    const weaponsLength = this.Weapons.length;
    for (let i = 0; i < weaponsLength; i++) {
      this.Weapons[i].ResetFiringTimer();
    }
  }
  GetPositionOutOfSight(e: number = -1, t: number = 0) {
    let i, s, n;
    if (
      (e <= 0
        ? (i = 2 * Math.PI * Math.random())
        : ((i = Math.atan2(
            this.Player.body!.velocity.y,
            this.Player.body!.velocity.x
          )),
          (i += (Math.random() - 0.5) * Phaser.Math.DegToRad(e))),
      t > 0)
    ) {
      var a = Math.cos(i);
      (s =
        this.Player.x + 0.5 * a * this.scene.renderer.width + Math.sign(a) * t),
        (a = Math.sin(i)),
        (n =
          this.Player.y +
          0.5 * a * this.scene.renderer.height +
          Math.sign(a) * t);
    } else
      (s = this.Player.x + 0.9 * Math.cos(i) * this.scene.renderer.width),
        (n = this.Player.y + 0.9 * Math.sin(i) * this.scene.renderer.height);
    return new Phaser.Math.Vector2(s, n);
  }

  MakeTreasure(x: number, y: number, bossIdAndValue?: RewardReference) {
    const treasure = this.pickupPool.SpawnAt(
      x,
      y,
      PickupType.TREASURE
    ) as TreasurePickup;

    treasure.SetTreasureConfig(bossIdAndValue);
    return treasure;
  }

  MakeGem(x: number, y: number, xp: number) {
    const gem = this.MakePickup(x, y, PickupType.GEM) as GemPickup;
    gem.SetValue(xp);
    return gem;
  }
  MakePickup(
    x: number,
    y: number,
    pickupType: PickupType = PickupType.COIN,
    s = PickupType.VOID
  ) {
    let pickup;
    let vector = new Phaser.Math.Vector2(x, y);
    if (x === null && y === null) {
      vector = this.GetPositionOutOfSight(90);
    }

    if (pickupType === PickupType.GEM) {
      pickup = this.gemsPool.SpawnAt(vector.x, vector.y, pickupType);
    } else {
      pickup = this.pickupPool.SpawnAt(vector.x, vector.y, pickupType);
    }

    if (pickup.itemType === PickupType.WEAPON && s !== PickupType.VOID) {
      pickup.SetWeaponType(s);
    }
    return pickup;
  }

  furthest<
    P extends Phaser.Physics.Arcade.Sprite,
    T extends Phaser.Physics.Arcade.Sprite
  >(player: P, pickups: T[]): T {
    let flag = -1;
    let furthest;
    const x = player.x;
    const y = player.y;
    for (let i = 0; pickups.length > i; i++) {
      var item = pickups[i];
      const squared = Phaser.Math.Distance.Squared(x, y, item.x, item.y);
      if (squared > flag) {
        furthest = item;
        flag = squared;
      }
    }
    return furthest;
  }

  ShowDamageAt(x: number, y: number, PPower: number) {
    this.damageNumberPool.SpawnAt(x, y, PPower);
  }
  async GameOver() {
    // Stop accepting pickups immediately to prevent late overlaps adding coins
    this.acceptPickups = false;

    let isLevelFinished = false;
    let endgameReward = 0;

    // Clean up resources to prevent memory leaks
    if (this.PfxEmitter_Pickups) {
      this.PfxEmitter_Pickups.destroy();
      this.PfxEmitter_Pickups = undefined;
    }

    // Clean up object pool manager
    if (this.poolManager) {
      this.poolManager.destroy();
    }

    // Clean up the magnet properly
    if (this.Magnet) {
      // Destroy existing magnet overlap collider if any
      try {
        this.magnetPickupOverlap?.destroy();
      } catch {}

      this.magnetPickupOverlap = null;
      this.magnetState = {
        radius: this.Magnet.radius,
      };

      this.Magnet.destroy();
      this.Magnet = null as unknown as Magnet; // Will be restored in restoreGameObjects if needed
    }

    try {
      const [_, __, success] = await Promise.allSettled([
        // Claim gift coins as a single batch
        this.claimGiftCoinsReward(),

        // Claim caravan coins as a single batch
        this.claimCaravanCoinsReward(),

        // Wait for the endgame reward claim to complete, but only if time is up
        this.claimEndgameReward(),
      ]);

      // Check if endgame claim was successful
      isLevelFinished =
        success.status === "fulfilled" && success.value === true;

      // If successful, get the endgame reward value
      if (isLevelFinished) {
        const endgame = this.StageService.getEndgameId();
        if (endgame) {
          endgameReward = endgame.value;
        }
      }
    } catch (error) {
      console.error("Failed to claim rewards:", error);
    }

    // Show game over screen with updated coins, level completion status, and endgame reward
    this.SceneManager.ShowGameOverScreen(
      this.PlayerOptions.RunCoins,
      isLevelFinished,
      endgameReward
    );
  }

  ClearTimeStop() {
    const enemiesLength = this.Enemies.length;
    for (let i = 0; i < enemiesLength; i++) {
      this.Enemies[i].ResumeFromTimeStop();
    }
    this.IsTimeStopped = false;
  }
  CheckForLevelUp() {
    const xpRequiredToLevelUp = this.LevelUpFactory.xpRequiredToLevelUp;

    if (this.Player.xp > xpRequiredToLevelUp) {
      this.Player.LevelUp();
      this.SwapToLevelUpScene();
      this.LootManager.RecalculateLoot();
      this.LevelUpFactory.calculateXPfactor();
      this.PlayerUI.Update();
      this.PlayerUI.UpdatePlayerLevel();
    }
  }
  SwapToLevelUpScene() {
    Game.Core.SceneManager.EnterLevelUp();
  }

  GetWeaponLevel(bulletType: WeaponType) {
    let weapon = this.Weapons.find((t) => t.bulletType === bulletType);
    if (weapon) {
      return weapon.level;
    }
    return 0;
  }

  LevelWeaponUp(weaponType: WeaponType, isLevelUp = true) {
    if (!WEAPONS[weaponType]) return;
    const weapon = this.Weapons.find((t) => t.bulletType === weaponType);

    if (weapon) {
      weapon.LevelUp();

      this.MainUI.AddWeaponIcon(weaponType);

      // Check for evolutions after leveling up
      this.checkForEvolutions();
    } else {
      this.AddWeapon(weaponType, isLevelUp);
      this.SetSeenWeapon(weaponType);
    }
  }
  MakeAndActivatePickup(pickupType: PickupType) {
    this.MakePickup(this.Player.x, this.Player.y, pickupType).GetTaken();
  }
  SetSeenWeapon(weaponType: WeaponType) {
    // throw new Error('Method not implemented.')
  }

  Update(delta: number) {
    profiler.markUpdateStart();

    // Performance optimization: limit object processing based on device performance
    // Limit bullet count
    const bulletChildren = this.BulletGroup.getChildren();
    while (bulletChildren.length > GameCore.MAX_BULLETS) {
      const oldestBullet = bulletChildren[0] as any;
      if (oldestBullet.DeSpawn) {
        oldestBullet.DeSpawn();
      } else {
        this.BulletGroup.remove(oldestBullet, true, true);
      }
    }

    // Use direct array access with length caching for better performance
    const pickupChildrenForUpdate = this.PickupGroup.getChildren();
    const pickupLength = pickupChildrenForUpdate.length;
    for (let i = 0; i < pickupLength; i++) {
      const pickup = pickupChildrenForUpdate[i] as BasePickup;
      if (pickup.Update) {
        pickup.Update();
      }
    }

    // Bullets don't need individual updates - they use physics and timers
    // Remove bullet update loop for performance

    // Cache array lengths and use regular for loops for better performance
    const weaponsLength = this.Weapons.length;
    for (let i = 0; i < weaponsLength; i++) {
      this.Weapons[i].Update(delta);
    }

    this.Player.Update(delta);
    this.Magnet?.Update(delta);
    this.BGMan.Update(delta);

    // Update all enemies every frame to ensure smooth movement
    // Avoid creating array copies - iterate directly over enemies array
    // Use reverse iteration to handle mid-loop removals safely
    for (let i = this.Enemies.length - 1; i >= 0; i--) {
      const enemy = this.Enemies[i];
      if (enemy && !enemy.isDead) {
        enemy.Update(delta);
      }
    }

    // Update spatial grids for efficient collision detection
    this.UpdateSpatialGrids();

    // Process spatial grid-based bullet-enemy collisions
    // This replaces the slower Phaser physics overlap system for bullets
    this.ProcessBulletEnemyCollisions();

    // Add formation updates after enemy updates
    this.formationManager?.update();

    // Add MainUI update after formation updates
    this.MainUI.update();

    profiler.markUpdateEnd();
    profiler.update(this);
  }

  selectCharacter(id: string) {
    const character = this.UserCharactersService?.getCharacter();
    if (character) {
      this.PlayerOptions.SelectedCharacterId = id;
    }
  }

  getCurrentCharacter() {
    return this.UserCharactersService?.getCharacter();
  }

  AddEnemy(enemy: Enemy) {
    this.Enemies.push(enemy);
    // Add to spatial grid for efficient queries
    this.enemySpatialGrid.addObject(enemy);
  }

  /**
   * Add bullet to spatial grid for collision detection
   */
  AddBullet(bullet: BaseBullet) {
    this.bulletSpatialGrid.addObject(bullet);
  }

  /**
   * Remove enemy from spatial grid when it dies/despawns
   */
  RemoveEnemy(enemy: Enemy) {
    const index = this.Enemies.indexOf(enemy);
    if (index !== -1) {
      this.Enemies.splice(index, 1);
    }
    this.enemySpatialGrid.removeObject(enemy);
  }

  /**
   * Remove bullet from spatial grid when it despawns
   */
  RemoveBullet(bullet: BaseBullet) {
    this.bulletSpatialGrid.removeObject(bullet);
  }

  /**
   * Find nearest enemy to a point using spatial grid
   */
  FindNearestEnemy(
    x: number,
    y: number,
    maxDistance: number = 500
  ): Enemy | null {
    return this.enemySpatialGrid.findNearest(
      x,
      y,
      (enemy) =>
        !enemy.isDead &&
        !enemy.IsTimeStopped &&
        this.getDistanceSquared(x, y, enemy.x, enemy.y) <=
          maxDistance * maxDistance
    );
  }

  /**
   * Find enemies within radius using spatial grid
   */
  FindEnemiesInRadius(
    x: number,
    y: number,
    radius: number,
    maxResults: number = 10
  ): Enemy[] {
    return this.enemySpatialGrid.findInRadius(x, y, radius, {
      maxResults,
      filterFunction: (enemy) => !enemy.isDead && !enemy.IsTimeStopped,
      sortByDistance: true,
    });
  }

  /**
   * Process bullet-enemy collisions using spatial grids
   */
  ProcessBulletEnemyCollisions(): void {
    const collisions = this.bulletSpatialGrid.findIntersections(
      this.enemySpatialGrid,
      32, // 32px collision radius
      {
        maxResults: 100, // Limit collisions per frame for performance
        filterFunction: (enemy) => !enemy.isDead && !enemy.IsTimeStopped,
      }
    );

    for (const collision of collisions) {
      const bullet = collision.objectA;
      const enemy = collision.objectB;

      // Check if bullet has already hit this enemy
      if (!bullet.HasAlreadyHitObject(enemy)) {
        // Get weapon for this bullet to apply damage
        const weapon = bullet.weapon;
        if (weapon) {
          enemy.GetDamaged(weapon.PPower, weapon.hitVFX, weapon.knockback);
        }
      }
    }
  }

  /**
   * Update spatial grids with current object positions
   */
  UpdateSpatialGrids(): void {
    // Update enemy positions in spatial grid
    for (const enemy of this.Enemies) {
      if (!enemy.isDead) {
        this.enemySpatialGrid.updateObjectPosition(enemy, enemy.x, enemy.y);
      }
    }

    // Update bullet positions in spatial grid
    const allBullets = this.bulletSpatialGrid.getAllObjects();
    for (const bullet of allBullets) {
      if (bullet.active) {
        this.bulletSpatialGrid.updateObjectPosition(bullet, bullet.x, bullet.y);
      }
    }
  }

  private getDistanceSquared(
    x1: number,
    y1: number,
    x2: number,
    y2: number
  ): number {
    const dx = x2 - x1;
    const dy = y2 - y1;
    return dx * dx + dy * dy;
  }

  /**
   * Claims a reward for the collected gift coins
   * @returns A promise that resolves to whether the claim was successful
   */
  async claimGiftCoinsReward(): Promise<boolean> {
    const giftCoins = this.StageService.getGiftCoinsId();

    if (!giftCoins || this.giftCoinsCollected <= 0) {
      return false;
    }

    // Limit the value to claim to the maximum allowed by giftCoins
    const valueToSend = Math.min(this.giftCoinsCollected, giftCoins.value);

    const success = await this.StageService.claim(giftCoins.id, valueToSend);

    return success;
  }

  /**
   * Claims a reward for the collected caravan coins
   * @returns A promise that resolves to whether the claim was successful
   */
  async claimCaravanCoinsReward(): Promise<boolean> {
    const caravan = this.StageService.getCaravan();
    if (!caravan || this.caravanCoinsCollected <= 0) {
      return false;
    }

    // Limit the value to claim to the maximum allowed by caravan
    const valueToSend = Math.min(this.caravanCoinsCollected, caravan.value);

    const success = await this.StageService.claim(caravan.id, valueToSend);

    return success;
  }

  /**
   * Claims a reward for a treasure
   * @param treasureId - The ID of the treasure
   * @returns A promise that resolves to whether the claim was successful
   */
  async claimTreasureReward(treasureId: string): Promise<boolean> {
    return this.StageService.claim(treasureId);
  }

  /**
   * Claims a reward for the endgame
   * @param remainingSeconds - The remaining seconds in the game
   * @returns A promise that resolves to whether the claim was successful
   */
  async claimEndgameReward(): Promise<boolean> {
    const remainingSeconds = this.getRemainingSeconds();

    // Only claim endgame reward if the remaining seconds is exactly 0
    if (remainingSeconds !== 0) {
      console.log(
        "Not claiming endgame reward because there's still time remaining:",
        remainingSeconds
      );
      return false;
    }

    const endgame = this.StageService.getEndgameId();
    if (!endgame) {
      console.error("No endgame ID available");
      return false;
    }

    const success = await this.StageService.claim(endgame.id);

    if (success) {
      // Add coins based on the endgame value
      this.PlayerOptions.AddCoins(endgame.value);
      this.MainUI.UpdateCoins();
    }

    return success;
  }

  // Check if any weapon evolution is available
  checkForEvolutions() {
    // Get all weapons defined in the WEAPONS object
    const weaponEntries = Object.entries(WEAPONS);
    const entriesLength = weaponEntries.length;

    for (let i = 0; i < entriesLength; i++) {
      const [weaponTypeStr, weaponData] = weaponEntries[i];
      const weaponType = weaponTypeStr as WeaponType;
      const baseWeapon = weaponData[0] as WeaponData;

      // Skip if not an evolution weapon or already owned
      if (!baseWeapon.isEvolution || this.hasWeapon(weaponType)) {
        continue;
      }

      // Check if the weapon has evolvesFrom property
      if (baseWeapon.evolvesFrom && Array.isArray(baseWeapon.evolvesFrom)) {
        const sourceWeapons = baseWeapon.evolvesFrom as WeaponType[];
        let canEvolve = true;

        // Check if all required source weapons are maxed out
        const sourceWeaponsLength = sourceWeapons.length;
        for (let j = 0; j < sourceWeaponsLength; j++) {
          const sourceType = sourceWeapons[j];
          const sourceWeaponLevel = this.GetWeaponLevel(sourceType);
          const maxLevel = WEAPONS[sourceType].length;

          if (sourceWeaponLevel < maxLevel) {
            canEvolve = false;
            break;
          }
        }

        // If all source weapons are maxed, show evolution scene
        if (canEvolve) {
          this.scene.scene.pause();
          this.scene.scene.launch(Scenes.EvolutionScene, {
            evolution: weaponType,
          });
          return; // Only show one evolution at a time
        }
      }
    }
  }

  hasWeapon(weaponType: WeaponType): boolean {
    return this.Weapons.some((weapon) => weapon.bulletType === weaponType);
  }

  /**
   * Calculates the remaining seconds for the current stage
   * @returns The number of seconds remaining in the stage
   */
  getRemainingSeconds(): number | null {
    if (!this.stageEndTime) {
      return null;
    }

    return Math.max(0, this.stageEndTime - this.SurvivedSeconds);
  }

  /**
   * Select a look for the character
   * @param lookId The ID of the look to select (from API LookIdEnum)
   */
  async selectLook(lookId: string): Promise<boolean> {
    if (!this.UserLooksService) {
      console.error("UserLooksService not available");
      return false;
    }

    const success = await this.UserLooksService.selectLook(lookId);

    // If the look was selected successfully and we have a character,
    // update the character with the new look
    if (success && this.UserCharactersService) {
      const character = this.UserCharactersService.getCharacter();
      if (character) {
        // Now the lookId from API can be used directly in the character
        await this.UserCharactersService.updateCharacter({ lookId });
      }
    }

    return success;
  }

  /**
   * Clears the preserved magnet state
   * This can be called when starting a completely new game session
   */
  clearMagnetState() {
    this.magnetState = null;
  }

  /**
   * Restores the PfxEmitter_Pickups and Magnet after they were destroyed during GameOver
   * This method should be called when the player revives to ensure these objects are functional again
   */
  restoreGameObjects() {
    // We are resuming the same run; allow pickups again
    this.acceptPickups = true;
    // Restore particle emitter if it was destroyed
    if (!this.PfxEmitter_Pickups) {
      this.PfxEmitter_Pickups = this.scene.add.particles(0, 0, undefined, {
        lifespan: 800,
        frequency: 100,
        maxParticles: 100,
        // No visible particles, just for game logic
        alpha: { start: 0, end: 0 },
      });
    }

    // Restore magnet functionality - recreate it properly if needed
    if (!this.Magnet || !this.Magnet.active) {
      // Create a new magnet instance and add it to the scene
      this.Magnet = new Magnet(this.scene, this.Player.x, this.Player.y);
      this.scene.add.existing(this.Magnet);

      // Restore magnet state if it was preserved from GameOver
      if (this.magnetState) {
        this.Magnet.radius = this.magnetState.radius;
        this.Magnet.RefreshSize();
      }

      // Re-establish the physics overlap for magnet and pickups. Destroy previous magnet overlap only.
      try {
        this.magnetPickupOverlap?.destroy();
      } catch {}

      this.magnetPickupOverlap = this.scene.physics.add.overlap(
        this.Magnet,
        this.PickupGroup,
        (magnet, pickup) => {
          // Type assertion to handle StaticBody
          const magnetObj = magnet as any;
          const pickupObj = pickup as any;
          this.onMagnetOverlapsPickup(magnetObj, pickupObj);
        }
      );
    }
  }
}
